// Cloudflare API Configuration - replaces Firebase
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://honest-lend-api.techiemark-2d7.workers.dev';

// API client for Cloudflare Workers
class CloudflareAPI {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Set authentication token
  setToken(token) {
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  // Get authentication token - always read fresh from localStorage
  getToken() {
    return localStorage.getItem('auth_token');
  }

  // Make authenticated API request
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();
    
    console.log(`API Request: ${options.method || 'GET'} ${endpoint}`, token ? '(authenticated)' : '(no token)');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      // Handle empty responses
      const text = await response.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Failed to parse response:', text);
        data = { error: 'Invalid response from server' };
      }
      
      if (!response.ok) {
        console.error(`API Error ${response.status}:`, data);
        throw new Error(data.error || `API request failed with status ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  // Authentication methods
  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (data.success && data.token) {
      this.setToken(data.token);
      console.log('Login successful, token stored');
    }
    
    return data;
  }

  async logout() {
    this.setToken(null);
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminLoginTime');
    return { success: true };
  }

  async verifyAuth() {
    const token = this.getToken();
    if (!token) {
      return { valid: false };
    }
    
    try {
      return await this.request('/api/auth/verify');
    } catch (error) {
      console.error('Auth verification failed:', error);
      return { valid: false };
    }
  }

  // Lead management methods
  async createLead(leadData) {
    return await this.request('/api/leads', {
      method: 'POST',
      body: JSON.stringify(leadData),
    });
  }

  async getLeads(params = {}) {
    const searchParams = new URLSearchParams(params);
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/leads?${queryString}` : '/api/leads';
    return await this.request(endpoint);
  }

  async deleteLead(leadId) {
    return await this.request(`/api/leads/${leadId}`, {
      method: 'DELETE',
    });
  }

  async deleteAllLeads(leadIds) {
    // Batch delete implementation
    const deletePromises = leadIds.map(id => this.deleteLead(id));
    const results = await Promise.allSettled(deletePromises);
    
    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    
    return {
      success: true,
      deleted: successful,
      failed: failed,
      total: leadIds.length
    };
  }

  async changePassword(currentPassword, newPassword) {
    return await this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }
}

// Create and export API instance
export const api = new CloudflareAPI();

// Export for backward compatibility with existing code
export const auth = {
  currentUser: null,
  onAuthStateChanged: (callback) => {
    // Simulate auth state checking
    const checkAuth = async () => {
      try {
        const token = api.getToken();
        if (!token) {
          callback(null);
          return;
        }
        const result = await api.verifyAuth();
        const user = result.valid ? result.user : null;
        callback(user);
      } catch (error) {
        callback(null);
      }
    };
    
    checkAuth();
    return () => {}; // Unsubscribe function
  },
  signInWithEmailAndPassword: async (email, password) => {
    const result = await api.login(email, password);
    if (!result.success) {
      throw new Error(result.error || 'Login failed');
    }
    return { user: result.user };
  },
  signOut: async () => {
    return await api.logout();
  }
};

// Dummy export for compatibility
export const db = null;
